export class DeferredMedia extends HTMLElement {
  isPlaying = false;
  mediaPoster = null;
  toggleMediaButton = null;
  autoplayObserver = null;
  autoplayVideo = null;
  autoplayVisible = false;

  connectedCallback() {
    this.mediaPoster = this.querySelector(
      '[data-ref="deferred-media-poster"]'
    );
    this.toggleMediaButton = this.querySelector(
      '[data-ref="toggle-media-button"]'
    );

    this.mediaPoster?.addEventListener(
      'click',
      this.showDeferredMedia
    );
    this.toggleMediaButton?.addEventListener(
      'click',
      this.handleToggleMedia
    );

    const template = this.querySelector('template');
    const video = template?.content.querySelector('video');
    const sources = video
      ? [
          video.getAttribute('src'),
          ...Array.from(
            video.querySelectorAll('source'),
            source => source.getAttribute('src')
          ),
        ]
      : [];

    const isTargetVideo = sources.some(source =>
      source?.includes('f38ef5dbfb524bc2849c31cfe3b67255')
    );

    if (!this.closest('product-media-gallery') || !isTargetVideo) {
      return;
    }

    this.autoplayObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          this.autoplayVisible =
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.5;

          if (this.autoplayVisible && !document.hidden) {
            this.startAutoplay();
          } else if (this.getAttribute('data-media-loaded')) {
            this.pauseMedia();
          }
        }
      },
      { threshold: [0, 0.5] }
    );

    this.autoplayObserver.observe(this);
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
  }

  disconnectedCallback() {
    this.autoplayObserver?.disconnect();

    this.mediaPoster?.removeEventListener(
      'click',
      this.showDeferredMedia
    );
    this.toggleMediaButton?.removeEventListener(
      'click',
      this.handleToggleMedia
    );

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    this.autoplayVideo?.removeEventListener(
      'play',
      this.handleVideoPlay
    );
    this.autoplayVideo?.removeEventListener(
      'pause',
      this.handleVideoPause
    );
    this.autoplayVideo?.removeEventListener(
      'ended',
      this.handleVideoPause
    );

    this.autoplayVideo = null;
  }

  handleToggleMedia = () => {
    this.toggleMedia();
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      if (this.getAttribute('data-media-loaded')) {
        this.pauseMedia();
      }
    } else if (this.autoplayVisible) {
      this.startAutoplay();
    }
  };

  handleVideoPlay = () => {
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  };

  handleVideoPause = () => {
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  };

  startAutoplay = () => {
    this.loadContent(false);

    const video = this.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    if (this.autoplayVideo !== video) {
      this.autoplayVideo = video;
      video.addEventListener('play', this.handleVideoPlay);
      video.addEventListener('pause', this.handleVideoPause);
      video.addEventListener('ended', this.handleVideoPause);
    }

    video.play().catch(() => {
      // Conserve les commandes si le navigateur bloque l'autoplay.
      this.isPlaying = !video.paused;
      this.updatePlayPauseHint(this.isPlaying);
    });
  };

  updatePlayPauseHint(isPlaying) {
    if (this.toggleMediaButton instanceof HTMLElement) {
      this.toggleMediaButton.classList.remove('hidden');

      const playIcon =
        this.toggleMediaButton.querySelector('.icon-play');

      if (playIcon) {
        playIcon.classList.toggle('hidden', isPlaying);
      }

      const pauseIcon =
        this.toggleMediaButton.querySelector('.icon-pause');

      if (pauseIcon) {
        pauseIcon.classList.toggle('hidden', !isPlaying);
      }
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.playMedia();
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')
      ?.content.firstElementChild?.cloneNode(true);

    const container = this.querySelector(
      '[data-ref="deferred-media-video"]'
    );

    if (!content || !container) return;

    const video =
      content instanceof HTMLVideoElement
        ? content
        : content.querySelector?.('video');

    // Prépare le mode muet avant l'insertion dans la page.
    if (!focus && video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
    }

    container.appendChild(content);
    this.setAttribute('data-media-loaded', 'true');

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.mediaPoster?.classList.add('deferred-media__playing');

    // Conserve aussi le sélecteur présent dans le code initial.
    this.querySelector(
      '[ref="deferred-media-poster"]'
    )?.classList.add('deferred-media__playing');

    if (focus && video?.autoplay) {
      video.play().catch(() => {
        video.controls = true;
      });
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );

      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      return;
    }

    const video = this.querySelector('video');
    if (!video) return;

    video.play().then(() => {
      this.isPlaying = !video.paused;
      this.updatePlayPauseHint(this.isPlaying);
    }).catch(() => {
      this.isPlaying = !video.paused;
      this.updatePlayPauseHint(this.isPlaying);
      video.controls = true;
    });
  }

  pauseMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }

    this.isPlaying = false;

    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(false);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}